import React, { useState } from "react";
import { clientService, CreateClientDto, ClientLanguage, validateOrigin } from "../services/client.service";
import { useToast } from "../context/ToastContext";
import ApiKeyModal from "./ApiKeyModal";
import "../styles/clients.css";

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

export default function CreateClientModal({ onClose, onCreated }: Props) {
    const { showToast } = useToast();
    const [name, setName] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [layoutColor, setLayoutColor] = useState("#4f46e5");
    const [type, setType] = useState("");
    const [language, setLanguage] = useState<ClientLanguage | "">("");
    const [allowedOrigins, setAllowedOrigins] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [newApiKey, setNewApiKey] = useState<string | null>(null);
    const [createdName, setCreatedName] = useState("");
    const [createdClientId, setCreatedClientId] = useState("");
    const [createdOrigins, setCreatedOrigins] = useState<string[]>([]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            setError("Name is required.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const payload: CreateClientDto = { name: name.trim() };
            if (displayName.trim()) payload.displayName = displayName.trim();
            if (layoutColor.trim()) payload.layoutColor = layoutColor.trim();
            if (type.trim()) payload.type = type.trim();
            if (language) payload.language = language;
            const origins = allowedOrigins
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
            for (const o of origins) {
                const err = validateOrigin(o);
                if (err) { setError(err); setLoading(false); return; }
            }
            if (origins.length > 0) payload.allowedOrigins = origins;
            const client = await clientService.create(payload);
            setCreatedName(client.name);
            setCreatedClientId(client.id);
            setCreatedOrigins(client.allowedOrigins || []);
            showToast("Client created successfully.");
            if (client.apiKey) {
                setNewApiKey(client.apiKey);
            } else {
                onCreated();
            }
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to create client.");
        } finally {
            setLoading(false);
        }
    };

    if (newApiKey) {
        return (
            <ApiKeyModal
                apiKey={newApiKey}
                keyType="production"
                clientName={createdName}
                clientId={createdClientId}
                allowedOrigins={createdOrigins}
                onClose={() => {
                    setNewApiKey(null);
                    onCreated();
                }}
            />
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Create Client</h2>
                <p className="modal-subtitle">Add a new client to your account</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="client-name">Name *</label>
                        <input
                            id="client-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="My Business"
                            autoFocus
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="client-display-name">Display Name</label>
                        <input
                            id="client-display-name"
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Shown on the chat widget (optional)"
                        />
                        <span className="modal-field-hint">If empty, the default "RMT-AI" will be displayed.</span>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="client-layout-color">Layout Color</label>
                        <div className="color-input-row">
                            <input
                                id="client-layout-color"
                                type="text"
                                value={layoutColor}
                                onChange={(e) => setLayoutColor(e.target.value)}
                            />
                            <input
                                type="color"
                                className="color-picker"
                                value={layoutColor}
                                onChange={(e) => setLayoutColor(e.target.value)}
                            />
                        </div>
                        <span className="modal-field-hint">Theme color for the chat widget.</span>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="client-type">Type</label>
                        <select
                            id="client-type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <option value="">Select a type (optional)</option>
                            <option value="enterprise">Enterprise</option>
                            <option value="startup">Startup</option>
                            <option value="personal">Personal</option>
                        </select>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="client-language">Language</label>
                        <select
                            id="client-language"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value as ClientLanguage | "")}
                        >
                            <option value="">Select a language (optional)</option>
                            <option value="fr">French</option>
                            <option value="en">English</option>
                            <option value="lb">Luxembourgish</option>
                        </select>
                    </div>

                    <div className="modal-field">
                        <label htmlFor="client-origins">Allowed Origins</label>
                        <textarea
                            id="client-origins"
                            value={allowedOrigins}
                            onChange={(e) => setAllowedOrigins(e.target.value)}
                            placeholder={"https://example.com\nhttps://app.example.com"}
                            rows={3}
                        />
                        <span className="modal-field-hint">One origin per line. Leave empty to allow all origins. Local origins (localhost, 127.0.0.1) are not allowed.</span>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? "Creating..." : "Create"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
